import apiClient from '../client.js'

async function getTicket(id) {
  const response = await apiClient.get(`/ticket/${id}`)
  return response.data.ticket
}

async function getActiveTickets(filters = {}) {
  const response = await apiClient.post('/ticket/search', filters)
  return {
    tickets: response.data.tickets,
    totalLength: response.data.totalLength,
    settingsHosts: response.data.settingsHosts,
  }
}

async function getArchivedTickets(filters = {}) {
  const response = await apiClient.post('/ticket/archived/search', filters)
  return {
    tickets: response.data.tickets,
    totalLength: response.data.totalLength,
    settingsHosts: response.data.settingsHosts,
  }
}

async function createTicket(payload) {
  return apiClient.post('/ticket', payload)
}

async function createTicketComment(id, body) {
  return apiClient.post(`/ticket/${id}/comment`, body)
}

async function createTicketAttachments(id, formData) {
  return apiClient.post(`/ticket/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
}

async function updateTicket(id, patch) {
  return apiClient.patch(`/ticket/${id}`, patch)
}

async function updateTicketStatus(id, status) {
  return apiClient.patch(`/ticket/${id}/status`, { status })
}

async function archiveTicket(id) {
  return apiClient.patch(`/ticket/${id}/archive`)
}

async function archiveTickets(ids) {
  return apiClient.patch('/ticket/bulk-archive', { ids })
}

async function restoreTicket(id) {
  return apiClient.patch(`/ticket/${id}/restore`)
}

async function deleteTicket(id) {
  return apiClient.delete(`/ticket/${id}`)
}

async function deleteTickets(ids) {
  return apiClient.delete('/ticket/bulk-delete', { data: { ids } })
}

async function deleteTicketAttachment(id, attachmentId) {
  return apiClient.delete(`/ticket/${id}/attachments/${encodeURIComponent(attachmentId)}`)
}

class TicketService {
  get = {
    /**
     * Load one ticket by id.
     *
     * @param {string} id
     * @returns {Promise<object>} Unwrapped from `response.data.ticket`.
     */
    async one(id) {
      return getTicket(id)
    },

    /**
     * Load active tickets matching optional query filters.
     *
     * @param {object} [filters={}]
     * @returns {Promise<{ tickets: object[], totalLength: number }>} Unwrapped from `response.data`.
     */
    async active(filters = {}) {
      return getActiveTickets(filters)
    },

    /**
     * Load archived tickets matching optional query filters.
     *
     * @param {object} [filters={}]
     * @returns {Promise<{ tickets: object[], totalLength: number }>} Unwrapped from `response.data`.
     */
    async archived(filters = {}) {
      return getArchivedTickets(filters)
    },
  }

  create = {
    /**
     * Create a ticket.
     *
     * @param {object} payload
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async ticket(payload) {
      return createTicket(payload)
    },

    /**
     * Add a text comment to a ticket.
     *
     * @param {string} id
     * @param {{ comment: string }} body
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async comment(id, body) {
      return createTicketComment(id, body)
    },

    /**
     * Attach files to a ticket.
     *
     * @param {string} id
     * @param {FormData} formData - Multipart body with the files under `files`.
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async attachments(id, formData) {
      return createTicketAttachments(id, formData)
    },
  }

  update = {
    /**
     * Patch one ticket.
     *
     * @param {string} id
     * @param {object} patch
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async ticket(id, patch) {
      return updateTicket(id, patch)
    },

    /**
     * Change a ticket's workflow status.
     *
     * @param {string} id
     * @param {string} status
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async status(id, status) {
      return updateTicketStatus(id, status)
    },
  }

  archive = {
    /**
     * Archive one ticket.
     *
     * @param {string} id
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async ticket(id) {
      return archiveTicket(id)
    },

    /**
     * Archive many tickets in one request.
     *
     * @param {string[]} ids
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async tickets(ids) {
      return archiveTickets(ids)
    },
  }

  restore = {
    /**
     * Restore one archived ticket.
     *
     * @param {string} id
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async ticket(id) {
      return restoreTicket(id)
    },
  }

  delete = {
    /**
     * Soft-delete one ticket.
     *
     * @param {string} id
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async ticket(id) {
      return deleteTicket(id)
    },

    /**
     * Soft-delete many tickets in one request.
     *
     * @param {string[]} ids
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async tickets(ids) {
      return deleteTickets(ids)
    },

    /**
     * Remove one attachment from a ticket.
     *
     * @param {string} id
     * @param {string} attachmentId
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async attachment(id, attachmentId) {
      return deleteTicketAttachment(id, attachmentId)
    },
  }
}

export default new TicketService()
